/**
 * Shared display strings for contributor hub + profile (matches editorial labels).
 */
function capitalizeRole(role: string): string {
  const trimmedRole = role.trim();
  if (/^writer$/i.test(trimmedRole)) return 'Writer';
  if (/^editor$/i.test(trimmedRole)) return 'Editor';
  if (/^reviewer$/i.test(trimmedRole)) return 'Reviewer';
  return trimmedRole.charAt(0).toUpperCase() + trimmedRole.slice(1);
}

export function formatContributorRoleLine(role: string): string {
  const trimmedRole = role.trim();
  if (/^writer$/i.test(trimmedRole)) return 'Contributing Writer';
  if (/^editor$/i.test(trimmedRole)) return 'Contributing Editor';
  if (/^reviewer$/i.test(trimmedRole)) return 'Contributing Reviewer';
  return trimmedRole;
}

/**
 * Formats multiple roles into a single display line.
 * e.g. ["Editor", "Writer"] → "Contributing Editor | Writer"
 */
export function formatContributorRolesLine(roles: string[]): string {
  if (roles.length === 0) return 'Contributor';
  if (roles.length === 1) return formatContributorRoleLine(roles[0]);
  const [first, ...rest] = roles;
  return [formatContributorRoleLine(first), ...rest.map(capitalizeRole)].join(
    ' | ',
  );
}

/** Small caps eyebrow on profile hero (e.g. REVIEWER) */
export function contributorProfileRoleEyebrow(role: string): string {
  const trimmedRole = role.trim();
  if (/reviewer/i.test(trimmedRole)) return 'REVIEWER';
  if (/editor/i.test(trimmedRole)) return 'EDITOR';
  if (/writer/i.test(trimmedRole)) return 'WRITER';
  return trimmedRole.replace(/\s+/g, ' ').toUpperCase();
}

export { formatHighlightFieldTitle } from '@highereducation/wordpress';

/**
 * Hub card subtitle: WP `contributors_career` when set, else
 * "Contributing Writer | Editor" from roles.
 */
export function contributorHubSubtitle(card: {
  roles: string[];
  careerDescription?: string;
}): string {
  const careerDescription = card.careerDescription?.trim();
  if (careerDescription) return careerDescription;
  return formatContributorRolesLine(card.roles);
}

/**
 * Hero line under the contributor name: WP `contributors_career` when set,
 * else "Contributing …" from roles joined with |.
 */
export function contributorHeroSubtitle(profile: {
  careerDescription?: string;
  roles: string[];
  career?: string;
}): string {
  const careerDescription = profile.careerDescription?.trim();
  if (careerDescription) return careerDescription;
  return formatContributorRolesLine(
    profile.roles.length > 0
      ? profile.roles
      : profile.career
      ? [profile.career]
      : ['Contributor'],
  );
}
